import { useQuery } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  ChevronLeft,
  Component,
  Loader2,
  Space,
  Tag as TagIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import query from "@/Utils/request/query";
import { TagConfig, TagResource } from "@/types/emr/tagConfig/tagConfig";
import tagConfigApi from "@/types/emr/tagConfig/tagConfigApi";

interface MultiFilterStyleTagSelectorProps {
  selected: TagConfig[];
  onChange: (tags: TagConfig[]) => void;
  resource: TagResource;
  facilityId?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  trigger?: React.ReactNode;
}

// Clean, minimal tag selector matching multi-filter design
export function MultiFilterStyleTagSelector({
  selected,
  onChange,
  facilityId,
  resource,
  className,
  disabled = false,
  isLoading = false,
  trigger,
}: MultiFilterStyleTagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [groupPopoverOpen, setGroupPopoverOpen] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [childDrawerOpen, setChildDrawerOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TagConfig | null>(null);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Fetch top-level tags (both instance and facility tags in one call)
  const { data: rootTags, isLoading: isLoadingRoot } = useQuery({
    queryKey: ["tags", resource, search, facilityId],
    queryFn: query.debounced(tagConfigApi.list, {
      queryParams: {
        resource,
        status: "active",
        ...(search ? { display: search } : { parent_is_null: true }),
        ...(facilityId && { facility: facilityId }),
      },
    }),
    enabled: open || mobileDrawerOpen,
  });

  // Fetch children for active group popover (both instance and facility tags in one call)
  const { data: childTags, isLoading: isLoadingChildren } = useQuery({
    queryKey: [
      "tags",
      resource,
      "parent",
      groupPopoverOpen || selectedGroup?.id,
      facilityId,
    ],
    queryFn: query(tagConfigApi.list, {
      queryParams: {
        resource,
        parent: groupPopoverOpen || selectedGroup?.id,
        status: "active",
        ...(facilityId && { facility: facilityId }),
      },
    }),
    enabled:
      (open && !!groupPopoverOpen) || (childDrawerOpen && !!selectedGroup),
  });

  // Select/deselect tag
  const handleSelect = (tag: TagConfig) => {
    // If tag has a parent, enforce single selection per group
    const parentId =
      tag.parent && typeof tag.parent === "object" && "id" in tag.parent
        ? tag.parent.id
        : undefined;

    const alreadySelectedInGroup = selected.find(
      (t) =>
        t.parent &&
        typeof t.parent === "object" &&
        "id" in t.parent &&
        t.parent.id === parentId,
    );

    const isCurrentlySelected = selected.some((t) => t.id === tag.id);

    if (isCurrentlySelected) {
      onChange(selected.filter((t) => t.id !== tag.id));
    } else {
      onChange([
        ...selected.filter((t) => t.id !== alreadySelectedInGroup?.id),
        tag,
      ]);
    }
  };

  const _handleGroupClick = (groupId: string) => {
    setGroupPopoverOpen(groupPopoverOpen === groupId ? null : groupId);
  };

  const handleMobileGroupClick = (group: TagConfig) => {
    setSelectedGroup(group);
    setChildDrawerOpen(true);
  };

  const handleMobileBack = () => {
    setChildDrawerOpen(false);
    setSelectedGroup(null);
  };

  const isSelected = (tag: TagConfig) => selected.some((t) => t.id === tag.id);

  // Render tag list for mobile
  const renderMobileTagList = (tags: TagConfig[], showGroups = true) => (
    <div className="space-y-1">
      {tags
        ?.filter((tag) => (showGroups ? tag.has_children : !tag.has_children))
        .map((tag) => (
          <div
            key={tag.id}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
            onClick={() => {
              if (tag.has_children) {
                handleMobileGroupClick(tag);
              } else {
                handleSelect(tag);
              }
            }}
          >
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected(tag)}
              data-state={isSelected(tag) ? "checked" : "unchecked"}
              className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
            >
              {isSelected(tag) && (
                <span className="flex items-center justify-center text-current transition-none">
                  <Check className="size-3.5" />
                </span>
              )}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {tag.has_children ? (
                <Component className="h-4 w-4 text-gray-600" />
              ) : (
                <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-blue-100 border-blue-300"></div>
              )}
              <span className="text-sm truncate">{tag.display}</span>
              {tag.has_children && (
                <Badge className="inline-flex items-center rounded-md border font-medium transition-colors gap-1.5 border-gray-300 bg-gray-100 text-gray-900 text-xs p-0.5 ml-auto">
                  {t("group")}
                </Badge>
              )}
            </div>
            {tag.has_children && (
              <ArrowRight className="h-4 w-4 text-gray-400" />
            )}
          </div>
        ))}
    </div>
  );

  // Common trigger button
  const triggerButton = trigger ? (
    trigger
  ) : (
    <Button
      variant="outline"
      className={cn(
        "h-10",
        selected.length > 0 && "border-blue-300 bg-blue-50 h-auto",
        className,
      )}
      disabled={disabled || isLoading}
    >
      <div className="flex items-center gap-2 min-w-0 w-full">
        {isLoading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <TagIcon className="h-3 w-3" />
        )}

        <div className="flex gap-1 flex-wrap min-w-0 w-full overflow-hidden">
          {isLoading ? (
            <span>{t("updating_tags")}</span>
          ) : selected.length > 0 ? (
            selected.slice(0, 3).map((t) => (
              <Badge
                key={t.id}
                className="
  bg-blue-100 text-blue-900 border-blue-300
  whitespace-normal
  break-words
  overflow-wrap-anywhere
"
              >
                {t.display}
              </Badge>
            ))
          ) : (
            <span>{t("add_tags")}</span>
          )}
          {selected.length > 3 && (
            <Badge className="bg-gray-100 text-gray-900 border-gray-300 shrink-0">
              +{selected.length - 3} {t("more")}
            </Badge>
          )}
        </div>
      </div>
    </Button>
  );

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile ? (
        <>
          <Drawer open={mobileDrawerOpen} onOpenChange={setMobileDrawerOpen}>
            <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
            <DrawerContent className="flex flex-col max-h-[85vh]">
              <DrawerHeader className="pb-3">
                <DrawerTitle className="flex items-center gap-2">
                  {t("manage_tags")}
                </DrawerTitle>
              </DrawerHeader>

              <div className="flex-1 overflow-hidden flex flex-col">
                {/* Search */}
                <div className="px-4 pb-3">
                  <input
                    type="text"
                    placeholder={t("search_tags")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs transition-colors file:border-0 file:bg-transparent focus:ring-primary-500 focus:border-primary-500 file:text-sm file:font-medium file:text-gray-950 placeholder:text-gray-500 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm duration-300 h-8 text-sm"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4">
                  {/* Selected Tags */}
                  {selected.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        {t("selected_tags")}
                      </div>
                      {selected.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelect(tag)}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="true"
                            data-state="checked"
                            className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
                          >
                            <span className="flex items-center justify-center text-current transition-none">
                              <Check className="size-3.5" />
                            </span>
                          </button>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {tag.parent && (
                              <Component className="h-3 w-3 text-gray-600" />
                            )}
                            <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-blue-100 border-blue-300"></div>
                            <span className="text-sm truncate">
                              {tag.display}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Tag Groups */}
                  {rootTags?.results &&
                    rootTags.results.filter((tag) => tag.has_children).length >
                      0 && (
                      <div className="mb-4">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          {t("tag_groups")}
                        </div>
                        {renderMobileTagList(rootTags.results, true)}
                      </div>
                    )}

                  {/* Other Tags */}
                  {rootTags?.results &&
                    rootTags.results.filter((tag) => !tag.has_children).length >
                      0 && (
                      <div className="mb-4">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                          {t("other_tags")}
                        </div>
                        {renderMobileTagList(rootTags.results, false)}
                      </div>
                    )}

                  {isLoadingRoot && (
                    <div className="py-8 text-sm text-gray-500 text-center">
                      {t("loading")}
                    </div>
                  )}

                  {!isLoadingRoot && !rootTags?.results?.length && (
                    <div className="py-8 text-sm text-gray-500 text-center">
                      {t("no_tags_group")}
                    </div>
                  )}
                </div>
              </div>
            </DrawerContent>
          </Drawer>

          {/* Child Tags Drawer */}
          <Drawer open={childDrawerOpen} onOpenChange={setChildDrawerOpen}>
            <DrawerContent className="flex flex-col max-h-[85vh]">
              <DrawerHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMobileBack}
                    className="p-1 h-8 w-8"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <DrawerTitle className="flex items-center gap-2">
                    <Component className="h-4 w-4" />
                    {selectedGroup?.display}
                  </DrawerTitle>
                </div>
              </DrawerHeader>

              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex-1 overflow-y-auto px-4">
                  {isLoadingChildren ? (
                    <div className="py-8 text-sm text-gray-500 text-center">
                      {t("loading")}
                    </div>
                  ) : childTags?.results?.length ? (
                    <div className="space-y-1">
                      {childTags.results.map((childTag) => (
                        <div
                          key={childTag.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleSelect(childTag)}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={isSelected(childTag)}
                            data-state={
                              isSelected(childTag) ? "checked" : "unchecked"
                            }
                            className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
                          >
                            {isSelected(childTag) && (
                              <span className="flex items-center justify-center text-current transition-none">
                                <Check className="size-3.5" />
                              </span>
                            )}
                          </button>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-green-100 border-green-300"></div>
                            <span className="text-sm truncate">
                              {childTag.display}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-sm text-gray-500 text-center">
                      {t("no_tags")}
                    </div>
                  )}
                </div>
              </div>
            </DrawerContent>
          </Drawer>
        </>
      ) : (
        /* Desktop Dropdown */
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[calc(100vw)] max-w-[calc(100vw-3rem)] sm:max-w-xs p-0"
            align="start"
          >
            <div className="p-0">
              {/* Header */}
              <div className="flex items-center gap-2 p-2 border-b border-gray-200">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="h-6 w-6 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">
                  {t("tags", { count: selected.length })}
                </span>
              </div>

              {/* Content */}
              <div className="p-2 max-h-[calc(100vh-28rem)] overflow-y-auto">
                {/* Search */}
                <input
                  type="text"
                  placeholder={t("search_tags")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-xs transition-colors file:border-0 file:bg-transparent focus:ring-primary-500 focus:border-primary-500 file:text-sm file:font-medium file:text-gray-950 placeholder:text-gray-500 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 md:text-sm duration-300 h-10 text-sm mb-2"
                />

                <div>
                  {/* Selected Tags */}
                  {selected.length > 0 && (
                    <>
                      <div className="px-2 py-0.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t("selected_tags")}
                      </div>
                      {selected.map((tag) => (
                        <div
                          key={tag.id}
                          className="focus:bg-gray-100 focus:text-gray-900 relative rounded-sm text-sm outline-hidden select-none flex items-center gap-2 px-2 py-2.5 cursor-pointer"
                          onClick={() => handleSelect(tag)}
                        >
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked="true"
                            data-state="checked"
                            className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
                          >
                            <span className="flex items-center justify-center text-current transition-none">
                              <Check className="size-3.5" />
                            </span>
                          </button>
                          <div className="flex items-center gap-2 max-w-xs truncate">
                            <span className="text-sm flex flex-row items-center gap-1 min-w-0">
                              {tag.parent && (
                                <Component className="h-3 w-3 text-black/80" />
                              )}
                              {tag.parent && (
                                <span className="flex gap-1 items-center flex-shrink-0">
                                  <span className="text-gray-700 truncate">
                                    {tag.parent.display}
                                  </span>
                                  <ArrowRight className="h-3 w-3 flex-shrink-0" />
                                </span>
                              )}
                              <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-blue-100 border-blue-300"></div>
                              <span className="truncate">{tag.display}</span>
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="bg-gray-200 -mx-1 my-1 h-px"></div>
                    </>
                  )}

                  {/* Tag Groups */}
                  {rootTags?.results &&
                    rootTags.results.filter((tag) => tag.has_children).length >
                      0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide mt-2">
                          {t("tag_groups")}
                        </div>
                        {rootTags?.results
                          ?.filter((tag) => tag.has_children)
                          .map((tag) => (
                            <div key={tag.id} className="relative">
                              <Popover
                                open={groupPopoverOpen === tag.id}
                                onOpenChange={(open) =>
                                  setGroupPopoverOpen(open ? tag.id : null)
                                }
                              >
                                <PopoverTrigger asChild>
                                  <div className="focus:bg-gray-100 focus:text-gray-900 cursor-default rounded-sm text-sm outline-hidden select-none flex items-center gap-2 px-2 py-2.5">
                                    <div className="flex items-center gap-2 flex-1 justify-between">
                                      <div className="flex items-center gap-1">
                                        <Component className="h-4 w-4 text-black/80" />
                                        <span className="text-sm">
                                          {tag.display}
                                        </span>
                                      </div>
                                      <Badge className="inline-flex items-center rounded-md border font-medium transition-colors gap-1.5 border-gray-300 bg-gray-100 text-gray-900 text-xs p-0.5">
                                        {t("group")}
                                      </Badge>
                                    </div>
                                    <ArrowRight className="ml-auto size-4" />
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-64 p-0"
                                  side="right"
                                  align="start"
                                  sideOffset={5}
                                >
                                  <div className="p-2 border-b border-gray-200">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      {tag.display}
                                    </div>
                                  </div>
                                  {isLoadingChildren ? (
                                    <div className="p-2 text-sm text-gray-500">
                                      {t("loading")}
                                    </div>
                                  ) : childTags?.results?.length ? (
                                    childTags.results.map((childTag) => (
                                      <div
                                        key={childTag.id}
                                        className="focus:bg-gray-100 focus:text-gray-900 relative rounded-sm text-sm outline-hidden select-none flex items-center gap-2 px-2 py-1 cursor-pointer"
                                        onClick={() => handleSelect(childTag)}
                                      >
                                        <button
                                          type="button"
                                          role="checkbox"
                                          aria-checked={isSelected(childTag)}
                                          data-state={
                                            isSelected(childTag)
                                              ? "checked"
                                              : "unchecked"
                                          }
                                          className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
                                        >
                                          {isSelected(childTag) && (
                                            <span className="flex items-center justify-center text-current transition-none">
                                              <Check className="size-3.5" />
                                            </span>
                                          )}
                                        </button>
                                        <div className="flex items-center gap-2 flex-1">
                                          <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-green-100 border-green-300"></div>
                                          <span className="text-sm">
                                            {childTag.display}
                                          </span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="p-2 text-sm text-gray-500">
                                      {t("no_tags")}
                                    </div>
                                  )}
                                </PopoverContent>
                              </Popover>
                            </div>
                          ))}
                        <div className="bg-gray-200 -mx-1 my-1 h-px"></div>
                      </>
                    )}

                  {/* Other Tags */}
                  {rootTags?.results &&
                    rootTags.results.filter((tag) => !tag.has_children).length >
                      0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wide mt-2">
                          {t("other_tags")}
                        </div>
                        {rootTags?.results
                          ?.filter((tag) => !tag.has_children)
                          .map((tag) => (
                            <div
                              key={tag.id}
                              className="focus:bg-gray-100 focus:text-gray-900 relative rounded-sm text-sm outline-hidden select-none flex items-center gap-2 px-2 py-2.5 cursor-pointer"
                              onClick={() => handleSelect(tag)}
                            >
                              <button
                                type="button"
                                role="checkbox"
                                aria-checked={isSelected(tag)}
                                data-state={
                                  isSelected(tag) ? "checked" : "unchecked"
                                }
                                className="bg-white peer border-gray-200 data-[state=checked]:bg-primary-600 data-[state=checked]:text-primary-100 data-[state=checked]:border-primary-600 focus-visible:border-primary-600 focus-visible:ring-primary-500/50 size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-4 w-4"
                              >
                                {isSelected(tag) && (
                                  <span className="flex items-center justify-center text-current transition-none">
                                    <Check className="size-3.5" />
                                  </span>
                                )}
                              </button>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <div className="h-3 w-3 rounded-full flex-shrink-0 border bg-blue-100 border-blue-300"></div>
                                <span className="text-sm truncate">
                                  {tag.display}
                                </span>
                              </div>
                            </div>
                          ))}
                      </>
                    )}

                  {isLoadingRoot && (
                    <div className="px-2 py-4 text-sm text-gray-500 text-center">
                      {t("loading")}
                    </div>
                  )}

                  {!isLoadingRoot && !rootTags?.results?.length && (
                    <div className="px-2 py-4 text-sm text-gray-500 text-center">
                      {t("no_tags_group")}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Navigation */}
              <div className="bg-gray-200 h-px"></div>
              <div className="flex justify-between items-center h-11">
                <div className="flex gap-1 my-3.5 mx-4">
                  <div className="bg-gray-100 shadow-full rounded-md px-1 border border-gray-300 flex items-center">
                    <ArrowUp className="size-3" />
                  </div>
                  <div className="bg-gray-100 shadow-full rounded-md px-1 border border-gray-300 flex items-center">
                    <ArrowDown className="size-3" />
                  </div>
                  <span className="text-xs text-gray-700 self-center">
                    {t("navigate")}
                  </span>
                </div>
                <div className="flex gap-1 my-3.5 mx-4">
                  <div className="bg-gray-100 shadow-full rounded-md px-1 border border-gray-300 flex items-center">
                    <Space className="size-3" />
                  </div>
                  <span className="text-xs text-gray-700 self-center">
                    {t("to_select")}
                  </span>
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}
